// FAQ content for the keyword landing pages. Each array renders on its page
// and is emitted as FAQPage JSON-LD at prerender time (routeMeta.ts).

import { FaqItem } from "@/data/faqItems";

export const idealWomanFaq: FaqItem[] = [
  {
    question: "What is an ideal woman calculator?",
    answer:
      "An ideal woman calculator is a statistical tool that estimates what percentage of US women match the criteria you select - age range, height, body type, income, education, marital status, and more. Ours uses real data from the US Census Bureau, CDC NHANES surveys, and Pew Research Center rather than made-up numbers.",
  },
  {
    question: "How many women meet my standards?",
    answer:
      "It depends entirely on how many filters you stack. Each preference is a percentage of the female population, and the calculator multiplies them together. For example, roughly 39% of US women aged 25-34 hold a bachelor's degree, and only about 7% of women earn $100,000 or more per year - combine those two filters and you are already below 3% of the population.",
  },
  {
    question: "Is this the same as a female delusion calculator?",
    answer:
      "It answers the same question - how realistic are your standards - but with a focus on transparent methodology. Every filter is backed by a cited government or research data source, and the full multiplicative probability model is documented on our methodology page.",
  },
  {
    question: "How accurate are the results?",
    answer:
      "Results are population-level estimates from large government surveys, so they are accurate as national averages but cannot account for your location, social circle, or personal appeal. The model also treats filters as statistically independent, which slightly overstates rarity when preferences are correlated (for example, education and income).",
  },
];

export const datingStandardsFaq: FaqItem[] = [
  {
    question: "What is a dating standards calculator?",
    answer:
      "A dating standards calculator estimates the probability that a potential partner meets all of the criteria you select - age, height, body type, income, education, and more. Each standard filters the US population using real US Census Bureau, CDC, and Pew Research data, so the result reflects how selective your combined standards actually are.",
  },
  {
    question: "How do I test my dating standards?",
    answer:
      "Pick the criteria that matter to you and run the calculation. This page tests standards for a female partner; if you are evaluating standards for a male partner, use our Male Partner Calculator, which applies the same model to male demographic data covering height, income, and education.",
  },
  {
    question: "What are the odds of finding my ideal partner?",
    answer:
      "For most people with several firm standards, the probability lands in the low single digits or below. That does not mean your ideal partner does not exist - even 1% of US women aged 18-44 is over half a million people - but it does show how much each additional requirement narrows the pool.",
  },
  {
    question: "Why do my standards lower my odds so much?",
    answer:
      "Because probabilities multiply. Two filters that each match 30% of the population combine to just 9%, and a third at 50% cuts that to 4.5%. Stacking even reasonable-sounding standards excludes the vast majority of people, which is why the numbers shrink faster than intuition suggests.",
  },
];

export const statesFaq: FaqItem[] = [
  {
    question: "Which state has the most single women?",
    answer:
      "California has the largest pool by far, with an estimated 6.8 million women aged 18-44 - about one in eight US women in that age range. Texas (about 5.6 million), New York, and Florida (each about 3.4 million) follow. Large pools do not guarantee better odds for any one preference, but they mean more absolute matches at the same percentage.",
  },
  {
    question: "How are the state dating pool numbers calculated?",
    answer:
      "Each estimate starts from the 2023 US Census Bureau state population figures and applies American Community Survey distributions for age, sex, education, and marital status. Counts are rounded to the nearest thousand and are meant for statistical comparison, not as exact headcounts.",
  },
  {
    question: "How do I turn a calculator result into a number for my state?",
    answer:
      "Multiply your national probability by your state's estimated women aged 18-44. A result of 1% in a state with 1,000,000 women in that range means roughly 10,000 matches. State education and marriage rates shift the real figure: a state with an above-average bachelor's rate will have more matches for an education filter than the national math suggests.",
  },
  {
    question: "Why do never-married rates differ so much between states?",
    answer:
      "Never-married shares track urbanization, college attainment, and how early people marry - not just age. Dense, highly educated places lead: about 55% of adult women in DC and 42% in New York have never married. Utah is the youngest state yet has the lowest rate (about 25%), alongside Idaho and Wyoming, because people there tend to marry earlier.",
  },
];
